import type { SemanticConfig, SemanticPalette } from './types';

/**
 * Builds a semantic palette from a SemanticConfig.
 *
 * There are ZERO light/dark conditionals here.  The mode is already baked
 * into the resolved `neutral` scale (woodsmoke mirrored for light mode):
 *
 *   shade 50   = highest-contrast foreground  (white in dark, black in light)
 *   shade 500  = mid-tone
 *   shade 1000 = deepest background           (black in dark, white in light)
 *
 * Each theme builds its own SemanticConfig, choosing which families fill
 * which roles (e.g. success = shamrock in one theme, blueribbon in another).
 */
export function buildSemanticPalette(config: SemanticConfig): SemanticPalette {
    const { neutral, primary, success, error } = config;

    return {
        layout: {
            page: neutral[950],
            header: neutral[1000],
            sidebar: neutral[1000],
            content: neutral[950],
            pageHeader: neutral[950],
        },

        card: {
            default: neutral[1000],
            elevated: neutral[950],
            alternate: neutral[900],
            inset: neutral[1000],
            selected: primary[950],
            border: {
                subtle: neutral[800],
                default: neutral[700],
                strong: neutral[600],
            },
        },

        grid: {
            bg: {
                container: neutral[1000],
                header: neutral[900],
                row: neutral[1000],
                rowHover: neutral[900],
                rowAlternate: neutral[950],
                rowSelected: primary[950],
                footer: neutral[900],
            },
            text: {
                header: neutral[400],
                sortedHeader: primary[500],
            },
        },

        line: {
            subtle: neutral[800],
            default: neutral[700],
            strong: neutral[600],
        },

        text: {
            primary: neutral[50],
            secondary: neutral[400],
            tertiary: neutral[500],
            disabled: neutral[600],
            inverse: neutral[1000],
        },

        progress: {
            track: neutral[800],
        },

        overlay: {
            surface: neutral[900],
            scrim: `${neutral[1000]}B3`,
        },

        data: {
            positive: [success[500], success[600], success[200]],
            negative: [error[500], error[600], error[200]],
            neutral: [neutral[500], neutral[400], neutral[800]],
        },

        state: {
            hover: neutral[900],
            disabledOpacity: 0.5,
        },
    };
}
